import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, Save, Check } from "lucide-react";
import { toast } from "sonner";
import { adminFetch, ADMIN_BACKEND_URL } from "./AdminLayout";

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await adminFetch(`${ADMIN_BACKEND_URL}/api/admin/products`);
      const data = await res.json();
      setProducts(data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (idx, field, value) => {
    const updated = [...products];
    updated[idx] = { ...updated[idx], [field]: field === "price" ? parseFloat(value) || 0 : value };
    setProducts(updated);
  };

  const saveProduct = async (product) => {
    setSaving((prev) => ({ ...prev, [product.id]: true }));
    try {
      const res = await adminFetch(
        `${ADMIN_BACKEND_URL}/api/admin/products/${product.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: product.name,
            description: product.description,
            price: product.price,
          }),
        }
      );
      if (!res.ok) throw new Error("Failed");
      toast.success(`${product.name} updated`);
    } catch {
      toast.error("Failed to update product");
    } finally {
      setSaving((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
      <div className="flex items-center gap-3 mb-1">
        <DollarSign className="w-6 h-6 text-sky-600" />
        <h1
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: "Manrope, sans-serif" }}
          data-testid="admin-pricing-headline"
        >
          Product Pricing
        </h1>
      </div>
      <p className="text-slate-500 text-sm mb-8">
        Changes take effect immediately on the public shop.
      </p>

      <div className="space-y-4">
        {products.map((product, idx) => (
          <div
            key={product.id}
            className="bg-white rounded-xl border border-slate-200 p-6"
            data-testid={`pricing-card-${product.id}`}
          >
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              {/* Image */}
              {product.image && (
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Fields */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500 block mb-1">Product Name</label>
                  <Input
                    value={product.name}
                    onChange={(e) => updateField(idx, "name", e.target.value)}
                    className="h-9"
                    data-testid={`product-name-${product.id}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={product.price}
                    onChange={(e) => updateField(idx, "price", e.target.value)}
                    className="h-9 font-mono"
                    min="0"
                    data-testid={`product-price-${product.id}`}
                  />
                </div>
              </div>

              {/* SKU + Save */}
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400 font-mono">{product.sku}</span>
                <Button
                  onClick={() => saveProduct(product)}
                  disabled={saving[product.id]}
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid={`save-product-${product.id}`}
                >
                  {saving[product.id] ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Description */}
            <div className="mt-3">
              <label className="text-xs text-slate-500 block mb-1">Description</label>
              <Input
                value={product.description}
                onChange={(e) => updateField(idx, "description", e.target.value)}
                className="h-9 text-sm"
                data-testid={`product-desc-${product.id}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
